import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useApiError } from '@/hooks/use-api-error';
import { captureError } from '@/lib/monitoring';
import { cn } from '@/lib/utils';

export interface RouteErrorBoundaryProps {
  error: unknown;
  /**
   * Rule CR4: tags which boundary caught this (e.g. `"customers-directory"`)
   * so a reference id actually narrows down where a crash happened, the same
   * way the root boundary already tags itself `"root"`.
   */
  boundary: string;
  /**
   * Rule CR5: a route-level boundary's recovery is scoped to that route, not
   * the whole app. Pass a re-navigate/re-invalidate callback for a feature
   * route; omitted, this falls back to a full reload, which is only actually
   * correct for the root boundary.
   */
  onRetry?: () => void;
  className?: string;
}

/**
 * Rule CR1-CR3: the one place a caught render error becomes what the user
 * sees - a translated message plus a reference id via `useApiError()`, never
 * a raw stack trace, component name, or file path. `routes/__root.tsx`'s own
 * boundary is the original, already-correct instance this extracts from
 * (byte-for-byte the same markup and hooks); every route-level boundary this
 * adds (rule CR2: at least the `_app` layout and each top-level feature
 * route) composes this instead of re-deriving the same markup, so "every new
 * boundary matches it" (rule CR3) is a property of the code, not a
 * convention someone has to remember.
 */
export function RouteErrorBoundary({
  error,
  boundary,
  onRetry,
  className,
}: RouteErrorBoundaryProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const toMessage = useApiError();
  const failure = toMessage(error);

  useEffect(() => {
    captureError(error, { boundary });
  }, [error, boundary]);

  return (
    <div
      className={cn(
        'flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center',
        className,
      )}
    >
      <p className="text-lg font-medium">{failure.message}</p>
      <Button onClick={onRetry ?? (() => globalThis.location.reload())}>
        {t('actions.retry')}
      </Button>
    </div>
  );
}
